import { ManufacturerName, SensorType } from "../shared.js";
import {
    generateEnviroProGSeriesConfiguration,
    generateHydrascoutConfiguration,
    generateSentekDrillAndDropConfiguration,
    generateTeros12Configuration,
    generateFloraPulseConfiguration,
    generateAquaCheckConfiguration,
    generateGroPointProbesConfiguration,
    generateWet150Configuration,
} from "./SDI12SensorClusterConfigurationTemplates.js";

export type SDI12SensorClusterConfigurationConnectedSensor = {
    sensorType: SensorType;
    depth?: number;
    /**
     * The byte index of this sensors data packet in the buffer (generated by the commandsToGetData)
     */
    sensorDataPositionInBuffer: number;
    /**
     * The number of bytes in a single sensor reading.
     */
    sensorDataLength: number;
    /**
     * Is the data ascii encoded (so 0x39 = 9) or hex encoded (so 0x39 = 57)
     */
    encodingType: "ascii" | "hex";

    /**
     * A regular expression to define exactly what segment of the select data (selected using sensorDataPositionInBuffer
     * and sensorDataLength) should be used as a number.
     *  - If a group is included in the regular expression, the value of the group will be used
     *  - Only the first match will be read as data. Any subsequent matches will be ignored
     *  - If nothing matches the regular expression, no data will be recorded
     */
    regexSelection?: RegExp;
};
export type SDI12SensorClusterConfigurationDataReadingCommand = {
    /**
     * the command that needs to be sent. This should use only ascii characters and should start with ADDR if the
     * address should be added to the beginning. So ADDRD0 would turn into 0D0 if the sensor cluster is at address 0
     */
    command: string;

    /**
     * the number of seconds that should be waited between this command sending and the next command sending
     */
    delay: number;
    /**
     * If true, the response will be checked with CRC to ensure that it has not been corrupted.
     */
    doCRCValidation: boolean;

    /**
     * Below is some typescript magic so that if you set readResponseData to true, you must also set
     * startOfByteRangeToRead and endOfByteRangeToRead
     */
} & (
    | {
          /**
           * Setting readResponseData to true means that the result of this command should be added to the buffer.
           * startOfByteRangeToRead and endOfByteRangeToRead indicate what range of bytes should be added to the buffer
           */
          readResponseDataToBuffer: true;
          /**
           * The index of the first byte that should be added to the buffer
           */
          startOfByteRangeToRead: number;
          /**
           * The index of the last byte that should be added to the buffer
           */
          endOfByteRangeToRead: number;
      }
    | {
          readResponseDataToBuffer: false;
      }
);

export type SDI12SensorClusterConfigurationBase = {
    /**
     * The full manufacturer name.
     */
    manufacturerName: ManufacturerName;

    /**
     * The model name as written by the manufacturer on their website or on the label for the device.
     * Will often be just a bunch of letters and numbers.
     * It should NOT include the manufacturer name
     */
    modelName: string;

    /**
     * Can be the model name if the model name is easy to understand. Otherwise it should describe the sensor.
     * It should be evident what kind of sensor it is, and what variant it is if there are multiple that Verdi
     * supports.
     * It should NOT include the manufacturer name
     */
    friendlyName: string;

    /**
     * Can be the same as the friendly name if the friendly name is short. Otherwise, it should communicate as much
     * info as possible about the sensor so it can be distinguished from others, while remaining under a soft 15 character
     * limit.
     */
    friendlyShortName: string;

    /**
     * The key for the icon that should be used to represent this sensor. If left blank, then the default for the sensor
     * category will be used instead. Acceptable keys are, as of this writing, not defined at all.
     */
    iconKey?: string;

    /**
     * A list of each sensor in this cluster and information on where it's data is found (as well as depth since
     * multi-depth probes are a large portion of Sensor Clusters)
     */
    connectedSensors: SDI12SensorClusterConfigurationConnectedSensor[];

    /**
     * A list of commands that need to be sent over SDI-12 to fetch data from sensors, as well as information on how
     * to parse the response
     */
    commandsToGetData: SDI12SensorClusterConfigurationDataReadingCommand[];
};
const SDI12SensorClusterConfigurationsNoExplicitKeys = {
    // Sentek drill and drop tri-scan probes
    SentekDrillAndDropTriScan48Inch: generateSentekDrillAndDropConfiguration(48, {
        hasTemperature: true,
        hasSalinity: true,
    }),
    SentekDrillAndDropTriScan36Inch: generateSentekDrillAndDropConfiguration(36, {
        hasTemperature: true,
        hasSalinity: true,
    }),
    SentekDrillAndDropTriScan24Inch: generateSentekDrillAndDropConfiguration(24, {
        hasTemperature: true,
        hasSalinity: true,
    }),
    SentekDrillAndDropTriScan12Inch: generateSentekDrillAndDropConfiguration(12, {
        hasTemperature: true,
        hasSalinity: true,
    }),
    SentekDrillAndDropTriScan4Inch: generateSentekDrillAndDropConfiguration(4, {
        hasTemperature: true,
        hasSalinity: true,
    }),

    // Sentek drill and drop moisture and temperature probes (no salinity)
    SentekDrillAndDropMoisture48Inch: generateSentekDrillAndDropConfiguration(48, {
        hasTemperature: true,
        hasSalinity: false,
    }),
    SentekDrillAndDropMoisture36Inch: generateSentekDrillAndDropConfiguration(36, {
        hasTemperature: true,
        hasSalinity: false,
    }),
    SentekDrillAndDropMoisture24Inch: generateSentekDrillAndDropConfiguration(24, {
        hasTemperature: true,
        hasSalinity: false,
    }),
    SentekDrillAndDropMoisture12Inch: generateSentekDrillAndDropConfiguration(12, {
        hasTemperature: true,
        hasSalinity: false,
    }),
    SentekDrillAndDropMoisture4Inch: generateSentekDrillAndDropConfiguration(4, {
        hasTemperature: true,
        hasSalinity: false,
    }),

    // Enviro pro G series standard
    EnviroProGSeriesStandard40Cm: generateEnviroProGSeriesConfiguration(16, {
        hasTemperature: true,
        hasSalinity: false,
    }),
    EnviroProGSeriesStandard80Cm: generateEnviroProGSeriesConfiguration(32, {
        hasTemperature: true,
        hasSalinity: false,
    }),
    EnviroProGSeriesStandard120Cm: generateEnviroProGSeriesConfiguration(48, {
        hasTemperature: true,
        hasSalinity: false,
    }),
    EnviroProGSeriesStandard160Cm: generateEnviroProGSeriesConfiguration(64, {
        hasTemperature: true,
        hasSalinity: false,
    }),

    // Enviro pro G series Pro
    EnviroProGSeriesPro40Cm: generateEnviroProGSeriesConfiguration(16, { hasTemperature: true, hasSalinity: true }),
    EnviroProGSeriesPro80Cm: generateEnviroProGSeriesConfiguration(32, { hasTemperature: true, hasSalinity: true }),
    EnviroProGSeriesPro120Cm: generateEnviroProGSeriesConfiguration(48, { hasTemperature: true, hasSalinity: true }),
    EnviroProGSeriesPro160Cm: generateEnviroProGSeriesConfiguration(64, { hasTemperature: true, hasSalinity: true }),

    // AquaCheck Probes
    AquaCheck900mm4sensor: generateAquaCheckConfiguration(900, 4, 200, { hasTemperature: true }),
    AquaCheck1200mm6sensor: generateAquaCheckConfiguration(1200, 6, 200, { hasTemperature: true }),

    // Hydrascout HSTI Standard Probes (Salinity to be released)
    // Length/sensor count is variable, will add custom entries when needed, for example, both a 80" and a 32" might have 4 sensors
    HSTIhydrascout32Inch4Sensor: generateHydrascoutConfiguration(
        32,
        4, // depth in inches, # of sensors
        { hasTemperature: true, hasSalinity: false, isSandy: false },
    ),
    HSTIhydrascout6Inch1Sensor: generateHydrascoutConfiguration(
        6,
        1, // depth in inches, # of sensors
        { hasTemperature: true, hasSalinity: false, isSandy: false },
    ),

    HSTIhydrascout32Inch4SensorSandy: generateHydrascoutConfiguration(
        32,
        4, // depth in inches, # of sensors
        { hasTemperature: true, hasSalinity: false, isSandy: true },
    ),

    FloraPulseMicrotensiometer: generateFloraPulseConfiguration(),

    // Teros 12 single depth sensors, from 4 to 12 inches
    Teros12_4inch: generateTeros12Configuration(4),
    Teros12_6inch: generateTeros12Configuration(6),
    Teros12_8inch: generateTeros12Configuration(8),
    Teros12_12inch: generateTeros12Configuration(12),

    // Wet150 single depth sensors, from 4 to 12 inches
    Wet150_default: generateWet150Configuration("bulk_ec", 6),
    Wet150_mineral: generateWet150Configuration("mineral", 6),
    Wet150_organic: generateWet150Configuration("organic", 6),
    Wet150_peatMix: generateWet150Configuration("peatMix", 6),
    Wet150_coir: generateWet150Configuration("coir", 6),
    Wet150_minWool: generateWet150Configuration("minWool", 6),

    // GroPoint GPLP-S-T multidepth soil moisture probes
    GroPoint_GPLP_S_T_2: generateGroPointProbesConfiguration(30, { hasTemperature: true }),
    GroPoint_GPLP_S_T_3: generateGroPointProbesConfiguration(45, { hasTemperature: true }),
    GroPoint_GPLP_S_T_4: generateGroPointProbesConfiguration(60, { hasTemperature: true }),
    GroPoint_GPLP_S_T_5: generateGroPointProbesConfiguration(75, { hasTemperature: true }),
    GroPoint_GPLP_S_T_6: generateGroPointProbesConfiguration(90, { hasTemperature: true }),
    GroPoint_GPLP_S_T_8: generateGroPointProbesConfiguration(120, { hasTemperature: true }),
} as const satisfies Record<string, SDI12SensorClusterConfigurationBase>;

export type SDI12SensorClusterType = keyof typeof SDI12SensorClusterConfigurationsNoExplicitKeys;

export type SDI12SensorClusterConfiguration = SDI12SensorClusterConfigurationBase & { key: SDI12SensorClusterType };

export const SDI12SensorClusterConfigurations: Record<SDI12SensorClusterType, SDI12SensorClusterConfiguration> =
    Object.fromEntries(
        Object.keys(SDI12SensorClusterConfigurationsNoExplicitKeys).map(
            (clusterType: SDI12SensorClusterType): any =>
                [clusterType, { ...SDI12SensorClusterConfigurationsNoExplicitKeys[clusterType], key: clusterType }] as [
                    SDI12SensorClusterType,
                    SDI12SensorClusterConfiguration,
                ],
        ),
    ) as Record<SDI12SensorClusterType, SDI12SensorClusterConfiguration>;
